import React, { useState } from "react";
import {
  PiToggleRightFill,
  PiToggleLeftLight,
} from "react-icons/pi";
import { FiDownload, FiX } from "react-icons/fi";
import {
  useDeleteUserMutation,
  useToggleUserStatusMutation,
} from "../../redux/features/user/userApi";
import { useNavigate } from "react-router-dom";

const Security = ({ data, onToggleStatus, canWrite, id }) => {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteUser] = useDeleteUserMutation();
  const [toggleUserStatus] = useToggleUserStatusMutation();
  const [isActiveState, setIsActiveState] = useState(data.isActive);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  const isAdmin = () => {
    const role = localStorage.getItem("role");
    return role === '"admin"' || role === "admin";
  };

  const HOME_PAGE = "/";
  const { proof } = data?.bank_info?.[0] || {};

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = proof;
    link.download = "bank_proof.jpg";
    link.click();
  };

  const handleDeleteUser = async () => {
    setIsDeleting(true);
    try {
      await deleteUser(id).unwrap();
      setShowDeleteModal(false);
      setDeleteSuccess(true);
      setTimeout(() => navigate(HOME_PAGE), 1000);
    } catch (error) {
      alert("Failed to delete user");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleBlock = async () => {
    if (!canWrite) return;
    setToggleLoading(true);
    try {
      await toggleUserStatus({ id }).unwrap();
      setIsActiveState((prev) => !prev);
    } catch {
      alert("Failed to update user status");
    } finally {
      setToggleLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="rounded-2xl shadow-md border border-gray-100 bg-gradient-to-br from-white to-gray-50 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold">User Security Panel</h2>
            <p className="text-xs opacity-80">Manage account status and access</p>
          </div>
          <div
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              isActiveState ? "bg-green-200 text-green-900" : "bg-red-200 text-red-900"
            }`}
          >
            {isActiveState ? "Active" : "Blocked"}
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6 space-y-8">
          {/* Account Status */}
          <div className="flex items-center justify-between border border-gray-200 rounded-xl p-4 bg-white hover:shadow-sm transition-shadow">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Account Status</h3>
              <p className="text-xs text-gray-500">Toggle to block/unblock this user</p>
            </div>
            {canWrite && isAdmin() && (
              <>
                {isActiveState ? (
                  <PiToggleRightFill
                    size={42}
                    className={`text-blue-600 cursor-pointer transition-all hover:scale-105 ${
                      toggleLoading ? "opacity-50 pointer-events-none" : ""
                    }`}
                    onClick={handleToggleBlock}
                  />
                ) : (
                  <PiToggleLeftLight
                    size={42}
                    className={`text-gray-400 cursor-pointer transition-all hover:scale-105 ${
                      toggleLoading ? "opacity-50 pointer-events-none" : ""
                    }`}
                    onClick={handleToggleBlock}
                  />
                )}
              </>
            )}
          </div>

          {/* Bank Proof */}
          {/* <div className="border border-gray-200 rounded-xl p-4 bg-white hover:shadow-sm transition-shadow">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">
              Bank Verification Proof
            </h3>
            {proof ? (
              <div className="flex flex-col items-center space-y-4">
                <div
                  className="relative group w-full h-48 bg-gray-100 border border-gray-200 rounded-lg overflow-hidden cursor-pointer"
                  onClick={() => setShowModal(true)}
                >
                  <img
                    src={proof}
                    alt="Bank Proof"
                    className="w-full h-full object-contain group-hover:opacity-90 transition-opacity"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all"></div>
                </div>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                >
                  <FiDownload className="w-4 h-4" /> Download Proof
                </button>
              </div>
            ) : (
              <div className="text-center text-gray-500 text-sm py-8">
                No proof document uploaded
              </div>
            )}
          </div> */}

          {/* Delete Section */}
          {canWrite && isAdmin() && (
            <div className="border border-gray-200 rounded-xl p-4 bg-white hover:shadow-sm transition-shadow">
              <button
                onClick={() => setShowDeleteModal(true)}
                className="w-full py-2 bg-red-600 hover:bg-red-700 text-white font-medium text-sm rounded-md transition-colors"
              >
                Delete User
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Proof Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-3xl overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-base font-semibold">Bank Document Preview</h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <FiX className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 max-h-[70vh] overflow-auto">
              <img src={proof} alt="Bank Proof" className="w-full h-auto rounded-lg" />
            </div>
            <div className="p-4 border-t flex justify-end">
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
              >
                <FiDownload className="w-4 h-4" /> Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-sm overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-base font-semibold text-gray-800">
                Confirm Deletion
              </h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <FiX className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 text-gray-700">
              Are you sure you want to delete this user? This action cannot be undone.
            </div>
            <div className="flex justify-end gap-2 border-t p-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={isDeleting}
                className={`px-4 py-2 text-sm rounded-md text-white ${
                  isDeleting
                    ? "bg-red-400 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {deleteSuccess && (
        <div className="fixed top-5 right-5 bg-green-500 text-white px-4 py-2 rounded-md shadow-md text-sm flex items-center gap-2">
          <span>User deleted successfully</span>
          <button onClick={() => setDeleteSuccess(false)}>×</button>
        </div>
      )}
    </div>
  );
};

export default Security;
